import { Injectable } from '@angular/core';
import { ReportingService } from '@gnv-swagger/web/secured';
import { catchError, EMPTY, map, mergeMap, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { BaseService } from 'app/services/base.service';
import { environment } from 'environments/environment';
import { SearchExportRequestModels$Json$Params } from 'swagger-gen/web-secured/fn/reporting/search-export-request-models-json';
import { ExportRequestModelPagedList } from 'swagger-gen/web-secured/models/export-request-model-paged-list';
import { extractFileNameFromContentDisposition } from 'app/shared/utils/files/file.utils';

@Injectable({
  providedIn: "root",
})
export class ExportModelRepo extends BaseService {
  private baseUrl = `${environment.apiUrl}/export-request/model`;

  constructor(
    private reportingService: ReportingService,
    private readonly translate: TranslateService,
    private readonly http: HttpClient) {
    super();
  }

  public searchExportModels = (
    params: SearchExportRequestModels$Json$Params
  ): Observable<ExportRequestModelPagedList> => this.reportingService.searchExportRequestModels$Json({...params, body: params.body || {}});

  public createModel(params: {name: string, file: File}) {
    return this.reportingService
    .createExportRequestModel$Response({body: {name: params.name}})
    .pipe(
      mergeMap(response => {
        if (response.headers.has('uid')) {
          return this.import({ modelUid: response.headers.get('uid') as string, file: params.file })
        }

        const errorMessage = this.translate.instant('export-template.errors.no-model-uid-found');
        this.dispatchFailureError(errorMessage);

        return EMPTY;
      })
    )
  }

  /**
   * Deletes the model whose name is passed as a parameter
   * @param modelUid
   * @returns null
   */
  public delete(modelUid: string) {
    return this.reportingService.deleteExportRequestModel({uid : modelUid });
  }

  /****************************************Rewriting the Swagger APIs******************************************************* */

  /**
   * Allows you to import the model file
   * @param params
   * @returns
   */
  import(params: {modelUid: string, file: Blob}): Observable<HttpResponse<void>> {
    const url = `${this.baseUrl}/${encodeURIComponent(String(params.modelUid))}/upload`;
    return this.http.post<void>(url, params.file, { headers: this.httpHeadersForExcel(), observe: 'response' })
    .pipe(
      catchError(error => {
        this.dispatchFailureError(`An error occurred while sending the file.`);
        return this.handleServerError(error)
    }));
  }

    /**
   * Download an Export request
   * @param uid
   * @returns
   */
    public downloadExportModel(
      uid: string
    ): Observable<{ blob: Blob; fileName: string }> {
      return this.download(uid).pipe(
        map((response) => ({
          blob: response.blob,
          fileName: extractFileNameFromContentDisposition(response.headers),
        })),
        catchError((error) => {
          this.dispatchFailureError(
            `An error occurred while downloading the Export Request.`
          );
          return this.handleServerError(error);
        })
      );
    }

    /**
     * Download an export model
     * @param uid The model uid. Must be a valid uid.
     * @return Success / failure
     */
    private download(
      uid: string
    ): Observable<{ blob: Blob; headers: HttpHeaders }> {
      const url = `${this.baseUrl}/${encodeURIComponent(uid)}/download`;

      return this.http
        .get(url, { responseType: "blob", observe: "response" })
        .pipe(
          map((response) => {
            return {
              blob: response.body as Blob,
              headers: response.headers,
            };
          })
        );
    }

}
